import { useState } from "react";
import { useMediaQuery } from "@mui/material";
import { DEFAULT_PRIMARY_COLOR } from "../component/Constant";
var currencyLocaleMap = {
    AED: "ar-AE", AFN: "fa-AF", ALL: "sq-AL", AMD: "hy-AM", ANG: "nl-AW",
    AOA: "pt-AO", ARS: "es-AR", AUD: "en-AU", AWG: "nl-AW", AZN: "az-AZ",
    BAM: "bs-BA", BBD: "en-BB", BDT: "bn-BD", BGN: "bg-BG", BHD: "ar-BH",
    BMD: "en-BM", BND: "ms-BN", BOB: "es-BO", BRL: "pt-BR", BSD: "en-BS",
    BTN: "dz-BT", BWP: "en-BW", BYR: "be-BY", BZD: "en-BZ",
    CAD: "en-CA", CDF: "fr-CD", CHF: "de-CH", CLP: "es-CL", CNY: "zh-CN",
    COP: "es-CO", CRC: "es-CR", CVE: "pt-CV", CZK: "cs-CZ",
    DJF: "fr-DJ", DKK: "da-DK", DOP: "es-DO", DZD: "ar-DZ",
    EGP: "ar-EG", ETB: "am-ET", EUR: "de-DE",
    FJD: "en-FJ", FKP: "en-FK",
    GBP: "en-GB", GEL: "ka-GE", GHS: "en-GH", GIP: "en-GI", GMD: "en-GM",
    GTQ: "es-GT", GYD: "en-GY",
    HKD: "zh-HK", HNL: "es-HN", HRK: "hr-HR", HTG: "fr-HT", HUF: "hu-HU",
    IDR: "id-ID", ILS: "he-IL", INR: "en-IN", ISK: "is-IS",
    JMD: "en-JM", JOD: "ar-JO", JPY: "ja-JP",
    KES: "en-KE", KGS: "ky-KG", KHR: "km-KH", KYD: "en-KY", KZT: "kk-KZ",
    LAK: "lo-LA", LBP: "ar-LB", LKR: "si-LK", LRD: "en-LR", LSL: "en-LS",
    MAD: "ar-MA", MDL: "ro-MD", MKD: "mk-MK", MMK: "my-MM", MNT: "mn-MN",
    MOP: "zh-MO", MUR: "en-MU", MVR: "dv-MV", MWK: "en-MW", MXN: "es-MX",
    MYR: "ms-MY", MZN: "pt-MZ",
    NAD: "en-NA", NGN: "en-NG", NIO: "es-NI", NOK: "nb-NO", NPR: "ne-NP",
    NZD: "en-NZ",
    OMR: "ar-OM",
    PAB: "es-PA", PEN: "es-PE", PGK: "en-PG", PHP: "en-PH", PKR: "ur-PK",
    PLN: "pl-PL", PYG: "es-PY",
    QAR: "ar-QA",
    RON: "ro-RO", RSD: "sr-RS", RUB: "ru-RU", RWF: "rw-RW",
    SAR: "ar-SA", SBD: "en-SB", SCR: "en-SC", SEK: "sv-SE", SGD: "en-SG",
    SHP: "en-SH", SLL: "en-SL", SOS: "so-SO", SRD: "nl-SR", STD: "pt-ST",
    SVC: "es-SV", SZL: "en-SZ",
    THB: "th-TH", TJS: "tg-TJ", TMT: "tk-TM", TND: "ar-TN", TOP: "to-TO",
    TRY: "tr-TR", TTD: "en-TT", TWD: "zh-TW", TZS: "sw-TZ",
    UAH: "uk-UA", UGX: "en-UG", USD: "en-US", UYU: "es-UY", UZS: "uz-UZ",
    VND: "vi-VN", VUV: "bi-VU",
    WST: "sm-WS",
    XAF: "fr-CM", XCD: "en-AG", XOF: "fr-BJ", XPF: "fr-PF",
    YER: "ar-YE",
    ZAR: "en-ZA", ZMW: "en-ZM",
};
/**
 * Formats a number as currency using the shop's currency code for locale-aware
 * thousand/decimal separators. Falls back gracefully when currency info is missing.
 *
 * @param amount - The numeric value to format
 * @param currency - ISO 4217 currency code from Redux (e.g. "INR", "USD")
 * @param shopCurrency - Display symbol string from Redux (e.g. "₹", "$") used as fallback
 */
export function formatAmount(amount, currency, shopCurrency) {
    var _a, _b, _c;
    var num = Number(amount);
    if (isNaN(num))
        return shopCurrency ? "".concat(shopCurrency, "0.00") : "0.00";
    if (currency) {
        var locale = (_a = currencyLocaleMap[currency]) !== null && _a !== void 0 ? _a : "en-US";
        try {
            var parts = new Intl.NumberFormat(locale, {
                style: "currency",
                currency: currency,
                currencySign: "standard",
            }).formatToParts(num);
            var symbol = (_c = (_b = parts.find(function (p) { return p.type === "currency"; })) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : "";
            var isNegative = parts.some(function (p) { return p.type === "minusSign"; });
            var numberStr = parts
                .filter(function (p) { return p.type !== "currency" && p.type !== "minusSign"; })
                .map(function (p) { return p.value; })
                .join("")
                .trim();
            return isNegative ? "-".concat(symbol).concat(numberStr) : "".concat(symbol).concat(numberStr);
        }
        catch (_d) {
            // unknown currency code — fall through to shopCurrency fallback
        }
    }
    // Fallback: no ISO code available, use shopCurrency symbol with basic formatting
    var formatted = num.toLocaleString("en-US");
    return shopCurrency ? "".concat(shopCurrency, " ").concat(formatted) : formatted;
}
export function isNullOrEmpty() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    var isNullOrEmpty = false;
    try {
        args === null || args === void 0 ? void 0 : args.forEach(function (str) {
            if (str === undefined || str === null || str.toString().trim() === "") {
                isNullOrEmpty = true;
            }
        });
        return isNullOrEmpty;
    }
    catch (e) {
        return true;
    }
}
export var useDeviceQueries = function () {
    var isIpadMini = useMediaQuery("(min-device-width: 768px) and (max-device-width: 834px) and (min-device-height: 1024px) and (max-device-height: 1112px) and (-webkit-min-device-pixel-ratio: 2), (min-device-width: 1024px) and (max-device-width: 1112px) and (min-device-height: 768px) and (max-device-height: 834px) and (-webkit-min-device-pixel-ratio: 2)");
    var isIpadAir3 = useMediaQuery("(min-device-width: 834px) and (max-device-width: 834px) and (min-device-height: 1112px) and (max-device-height: 1112px) and (-webkit-min-device-pixel-ratio: 2), (min-device-width: 1112px) and (max-device-width: 1112px) and (min-device-height: 834px) and (max-device-height: 834px) and (-webkit-min-device-pixel-ratio: 2)");
    var isIpadAir4 = useMediaQuery("(min-device-width: 820px) and (max-device-width: 820px) and (min-device-height: 1180px) and (max-device-height: 1180px) and (-webkit-min-device-pixel-ratio: 2), (min-device-width: 1180px) and (max-device-width: 1180px) and (min-device-height: 820px) and (max-device-height: 820px) and (-webkit-min-device-pixel-ratio: 2)");
    var isIpadPro11 = useMediaQuery("(min-device-width: 834px) and (max-device-width: 834px) and (min-device-height: 1194px) and (max-device-height: 1194px) and (-webkit-min-device-pixel-ratio: 2), (min-device-width: 1194px) and (max-device-width: 1194px) and (min-device-height: 834px) and (max-device-height: 834px) and (-webkit-min-device-pixel-ratio: 2)");
    var isIpadPro129 = useMediaQuery("(min-device-width: 1024px) and (max-device-width: 1024px) and (min-device-height: 1366px) and (max-device-height: 1366px) and (-webkit-min-device-pixel-ratio: 2), (min-device-width: 1366px) and (max-device-width: 1366px) and (min-device-height: 1024px) and (max-device-height: 1024px) and (-webkit-min-device-pixel-ratio: 2)");
    var isMobile = useMediaQuery("(max-width: 767px)");
    var isMobileLandscape = useMediaQuery("(max-height: 500px) and (orientation: landscape)");
    var isMobileIpad = isIpadMini || isIpadAir3 || isIpadAir4 || isIpadPro11 || isIpadPro129 || isMobile || isMobileLandscape;
    return isMobileIpad;
};
/**
 * Full-screen toggle shared by Navbar and LoginComponent: hides/shows the Shopify storefront
 * chrome by adding/removing the `st-hide-shopify-chrome` class on <html> (App injects the
 * stylesheet that acts on it). Each caller keeps its own local `fullScreen` state since Navbar
 * and LoginComponent never render at the same time.
 */
export var useFullScreenToggle = function () {
    var _a = useState(typeof document !== "undefined" && document.documentElement.classList.contains("st-hide-shopify-chrome")), fullScreen = _a[0], setFullScreen = _a[1];
    var toggleFullScreen = function () {
        var next = !fullScreen;
        setFullScreen(next);
        document.documentElement.classList.toggle("st-hide-shopify-chrome", next);
    };
    return { fullScreen: fullScreen, toggleFullScreen: toggleFullScreen };
};
/** Returns the rep to the storefront (exits the portal overlay). */
export var goToWebsite = function () {
    window.location.href = window.location.origin;
};
/** Store's customized brand/theme colour (same source the login page uses); falls back to the app default. */
export var getThemePrimaryColor = function () {
    var _a, _b;
    try {
        var raw = (_a = window === null || window === void 0 ? void 0 : window.salesteam_bo_props) === null || _a === void 0 ? void 0 : _a.loginCustomization;
        var parsed = raw ? JSON.parse(raw) : undefined;
        return ((_b = parsed === null || parsed === void 0 ? void 0 : parsed.Theme) === null || _b === void 0 ? void 0 : _b.PrimaryColor) || DEFAULT_PRIMARY_COLOR;
    }
    catch (_c) {
        return DEFAULT_PRIMARY_COLOR;
    }
};
